import { ProductType } from "@/common/course/types";
import { ProductSearchListItemProps } from "@/components/ProductSearch/components/v2/ProductSearchListItem";
import normalizeHref from "@/utils/functions/normalizeHref";
import { normalizePartnerDisplayName } from "@/utils/functions/normalizePartnerDisplayName";
import getProductSummaryFlexibility from "@/lib/productSummary/helper/getProductSummaryFlexibility";
import {
  analyticsSkillsFromAlgoliaHit,
  formatAnalyticsDurationWeeks,
  formatAnalyticsListField,
} from "./analyticsFormatters";

/**
 * Maps an Algolia hit to ProductSearchListItem props format
 */
export const mapAlgoliaHitToListItem = (
  hit: Record<string, unknown>,
  index: number = 0,
): ProductSearchListItemProps => {
  const partnerKey =
    (hit.partnerKeys as string[] | undefined)?.[0] ||
    (hit.partner_keys as string[] | undefined)?.[0] ||
    "";
  const partnerNameRaw =
    hit.partnerName ??
    (hit._highlightResult as { partner?: { value?: string }[] } | undefined)
      ?.partner?.[0]?.value ??
    partnerKey;

  const partnerDisplayName = normalizePartnerDisplayName(
    partnerNameRaw as string | string[] | undefined | null,
    partnerKey,
  );

  const rawHref =
    ((hit.productSlug as string | undefined) ??
      (hit.marketing_url as string | undefined)) ||
    "";
  const href = normalizeHref(rawHref);

  const hl = hit._highlightResult as
    | {
        productName?: { value?: string };
        title?: { value?: string };
      }
    | undefined;
  const productTitleHtmlCandidate = hl?.productName?.value ?? hl?.title?.value;
  const productTitleHtml =
    typeof productTitleHtmlCandidate === "string" &&
    productTitleHtmlCandidate.trim() !== ""
      ? productTitleHtmlCandidate
      : undefined;

  const productNamePlain =
    ((hit.productName ?? hit.title) as string | undefined)?.toString?.() ?? "";

  const weeksToComplete =
    (hit.weeksToComplete as number | null | undefined) ??
    (hit.weeks_to_complete as number | null | undefined);

  const analyticsDurationDisplay =
    formatAnalyticsDurationWeeks(weeksToComplete);

  const flex = getProductSummaryFlexibility(
    hit.flexibility as string[] | string | null | undefined,
  );

  const skillsDisplay = analyticsSkillsFromAlgoliaHit(hit);
  const levelDisplay = formatAnalyticsListField(hit.level);

  return {
    id: (hit.productUuid || hit.uuid || hit.objectID) as string,
    productName: productNamePlain,
    productTitleHtml,
    productCategory:
      (hit.attributes as string[] | undefined)?.[0] ??
      (hit.subject as string[] | undefined)?.[0] ??
      "",
    partner: {
      key: partnerKey as string,
      fullName: partnerDisplayName,
    },
    partnerLogoUrl: (hit.partnerLogoOverride ||
      hit.partnerLogoUrl ||
      (hit.owners as { logoImageUrl?: string }[])?.[0]?.logoImageUrl ||
      "") as string,
    timeToComplete: weeksToComplete ?? undefined,
    href,
    index,
    variant: (hit.product ?? hit.productType ?? ProductType.COURSE) as string,
    analyticsDurationDisplay,
    analyticsLevel: levelDisplay !== "" ? levelDisplay : undefined,
    analyticsFlexibility: flex ? String(flex) : undefined,
    analyticsSkillsDisplay: skillsDisplay !== "" ? skillsDisplay : undefined,
  };
};

/**
 * Maps an array of Algolia hits to ProductSearchListItem props
 */
export const mapAlgoliaHitsToListItems = (
  hits: Record<string, unknown>[],
): ProductSearchListItemProps[] => {
  return hits.map((hit, index) => mapAlgoliaHitToListItem(hit, index));
};
